import { z } from "zod";

/** ISO 4217 code, e.g. USD, EUR, CLP, VES. */
export const currencySchema = z.string().regex(/^[A-Z]{3}$/, "Expected an ISO 4217 currency code");
export type Currency = z.infer<typeof currencySchema>;

/** An amount in the currency's minor units (cents for USD, whole pesos for CLP). */
export const moneySchema = z.object({ amount: z.number().int(), currency: currencySchema });
export type Money = z.infer<typeof moneySchema>;

const digitsCache = new Map<string, number>();

/** Number of minor-unit digits for a currency (USD 2, CLP 0). */
export function currencyDigits(currency: Currency): number {
  const cached = digitsCache.get(currency);
  if (cached !== undefined) return cached;
  const digits = new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
  digitsCache.set(currency, digits);
  return digits;
}

export function toMinor(major: number, currency: Currency): number {
  return Math.round(major * 10 ** currencyDigits(currency));
}

export function toMajor(minor: number, currency: Currency): number {
  return minor / 10 ** currencyDigits(currency);
}

/** Formats money for display. USD and EUR use symbols; other currencies show their code. */
export function formatMoney(money: Money, locale = "en-US"): string {
  const symbolic = money.currency === "USD" || money.currency === "EUR";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: money.currency,
    currencyDisplay: symbolic ? "narrowSymbol" : "code"
  }).format(toMajor(money.amount, money.currency));
}

/** Sums of money keyed by currency. Currencies are never converted or combined. */
export type MoneyByCurrency = Record<Currency, number>;

export function addMoney(totals: MoneyByCurrency, money: Money): MoneyByCurrency {
  return { ...totals, [money.currency]: (totals[money.currency] ?? 0) + money.amount };
}
